import { useMemo } from "react";

import {
	draftSenderBlock,
	hasDraftChannelMismatch,
	hasMissingSubject,
} from "./draftValidation.util";
import { Draft } from "./inboxDraft.types";
import { inviteNoteErrorLimit } from "./inviteNoteSeverity.util";
import { resolveSenderTier } from "./resolveSenderTier.util";

import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";

interface DraftSendBlockers {
	/** A sendable draft's sender and recipient are on different channels. */
	hasAnyChannelMismatch: boolean;
	/** A sendable email draft has no subject. */
	hasAnyMissingSubject: boolean;
	/** An invite note is longer than its sender's LinkedIn tier allows. */
	hasAnyInviteNoteOverflow: boolean;
	/** A sendable draft has no real sender, or one that cannot send (PREC-4312). */
	hasAnySenderBlock: boolean;
	/** Any of the above: Send is disabled and `handleSendMessage` sends nothing. */
	isBlocked: boolean;
}

/**
 * What keeps the chat composer's Send disabled — and `handleSendMessage` from sending — in one
 * place (extracted from `useAddInboxChat`, which may not grow). Only sendable drafts count for the
 * channel and subject checks; an invite note counts for its length even when empty.
 */
export const useDraftSendBlockers = (
	drafts: Draft[],
	isSendableDraft: (draft: Draft) => boolean,
	senderOptions: SenderRecipientOption[]
): DraftSendBlockers => {
	const hasAnyChannelMismatch = useMemo(
		() => drafts.some((d) => isSendableDraft(d) && hasDraftChannelMismatch(d)),
		[drafts, isSendableDraft]
	);

	const hasAnyMissingSubject = useMemo(
		() => drafts.some((d) => isSendableDraft(d) && hasMissingSubject(d)),
		[drafts, isSendableDraft]
	);

	// Tier-aware LinkedIn hard limit.
	const hasAnyInviteNoteOverflow = useMemo(
		() =>
			drafts.some(
				(d) =>
					!!d.isInviteNote &&
					d.content.length > inviteNoteErrorLimit(resolveSenderTier(d.from?.userId, senderOptions))
			),
		[drafts, senderOptions]
	);

	const hasAnySenderBlock = useMemo(
		() => drafts.some((d) => isSendableDraft(d) && !!draftSenderBlock(d, senderOptions)),
		[drafts, isSendableDraft, senderOptions]
	);

	return {
		hasAnyChannelMismatch,
		hasAnyMissingSubject,
		hasAnyInviteNoteOverflow,
		hasAnySenderBlock,
		isBlocked:
			hasAnyChannelMismatch ||
			hasAnyMissingSubject ||
			hasAnyInviteNoteOverflow ||
			hasAnySenderBlock,
	};
};
